import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  LayoutList,
  SearchCheck,
  Target,
  X,
} from 'lucide-react';

const DashboardOnboarding = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [closed, setClosed] = useState(false);

  const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
  const search = location.search || '';
  const createAppPath = (path) => `${languagePrefix}/app/${path}${search}`;

  const handleDismiss = () => {
    setClosed(true);
  };

  if (closed) {
    return null;
  }

  const steps = [
    {
      icon: SearchCheck,
      title: t('app:dashboard.onboarding.findInvestorsTitle'),
      text: t('app:dashboard.onboarding.findInvestorsText'),
      href: createAppPath('find-investors'),
    },
    {
      icon: Target,
      title: t('app:dashboard.onboarding.crmTitle'),
      text: t('app:dashboard.onboarding.crmText'),
      href: createAppPath('crm'),
    },
    {
      icon: LayoutList,
      title: t('app:dashboard.onboarding.dealsTitle'),
      text: t('app:dashboard.onboarding.dealsText'),
      href: createAppPath('deals'),
    },
    {
      icon: Bot,
      title: t('app:dashboard.onboarding.aiFundingTitle'),
      text: t('app:dashboard.onboarding.aiFundingText'),
      href: createAppPath('ai-funding'),
    },
  ];

  return (
    <section className="mb-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="max-w-2xl">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('app:dashboard.onboarding.badge')}
          </div>
          <h2 className="text-xl font-semibold tracking-tight text-stone-950">
            {t('app:dashboard.onboarding.title')}
          </h2>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t('app:dashboard.onboarding.subtitle')}
          </p>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('app:dashboard.onboarding.dismiss')}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {steps.map(({ icon: Icon, title, text, href }) => (
          <Link
            key={title}
            to={href}
            className="group flex min-h-[132px] flex-col justify-between rounded-lg border border-stone-200 bg-stone-50/70 p-4 transition-colors hover:border-stone-300 hover:bg-white"
          >
            <div>
              <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white text-stone-700 shadow-sm ring-1 ring-stone-200">
                <Icon className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-stone-950">{title}</h3>
              <p className="mt-1 text-xs leading-5 text-stone-600">{text}</p>
            </div>
            <span className="mt-3 inline-flex items-center text-xs font-medium text-stone-900">
              {t('app:dashboard.onboarding.open')}
              <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-5">
        <Link
          to={createAppPath('find-investors')}
          className="inline-flex h-7.5 w-auto max-w-full items-center justify-center gap-2 rounded-full bg-stone-900 px-4 text-xs font-medium text-white shadow transition-colors hover:bg-stone-900/90"
        >
          <span>{t('app:dashboard.onboarding.primaryCta')}</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
};

export default DashboardOnboarding;
